import { useState } from "react";

interface Endpoint {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  category: string;
  description: string;
  sampleRequest?: string;
  sampleResponse: string;
}

const endpoints: Endpoint[] = [
  {
    method: "POST",
    path: "/customers",
    category: "Customers",
    description: "Create a new customer (individual or business)",
    sampleRequest: `{
  "platformCustomerId": "user-001",
  "customerType": "INDIVIDUAL",
  "individual": {
    "fullName": "Alice Johnson",
    "email": "alice@example.com",
    "birthDate": "1990-01-15",
    "nationality": "US"
  },
  "address": {
    "line1": "123 Main St",
    "city": "San Francisco",
    "state": "CA",
    "postalCode": "94102",
    "country": "US"
  }
}`,
    sampleResponse: `{
  "id": "cust_01HQ3X5K7N2M8P",
  "platformCustomerId": "user-001",
  "customerType": "INDIVIDUAL",
  "kycStatus": "PENDING_REVIEW",
  "umaAddress": "$alice@partner.grid.com",
  "createdAt": "2025-10-20T10:30:00Z"
}`,
  },
  {
    method: "GET",
    path: "/customers",
    category: "Customers",
    description: "List all customers with optional filters",
    sampleResponse: `{
  "data": [
    {
      "id": "cust_01HQ3X5K7N2M8P",
      "fullName": "Alice Johnson",
      "customerType": "INDIVIDUAL",
      "kycStatus": "APPROVED",
      "umaAddress": "$alice@partner.grid.com"
    }
  ],
  "pagination": { "hasMore": false }
}`,
  },
  {
    method: "POST",
    path: "/customers/external-accounts",
    category: "Accounts",
    description: "Create an external account (bank, crypto wallet, etc.)",
    sampleRequest: `{
  "customerId": "cust_01HQ3X5K7N2M8P",
  "accountType": "US_ACCOUNT",
  "currency": "USD",
  "accountNumber": "123456789",
  "routingNumber": "021000021",
  "beneficiary": {
    "type": "INDIVIDUAL",
    "fullName": "Alice Johnson"
  }
}`,
    sampleResponse: `{
  "id": "ea_01HQ5R1A2B3C",
  "customerId": "cust_01HQ3X5K7N2M8P",
  "status": "ACTIVE",
  "accountType": "US_ACCOUNT",
  "currency": "USD",
  "accountLabel": "US Account ****6789"
}`,
  },
  {
    method: "GET",
    path: "/customers/internal-accounts",
    category: "Accounts",
    description: "List internal accounts (auto-provisioned wallets)",
    sampleResponse: `{
  "data": [
    {
      "id": "ia_01",
      "customerId": "cust_01HQ3X5K7N2M8P",
      "balance": { "amount": 1523400, "currency": "USD" },
      "capabilities": ["CUSTODY", "TRANSFER_IN", "TRANSFER_OUT"]
    }
  ]
}`,
  },
  {
    method: "POST",
    path: "/quotes",
    category: "Transfers",
    description: "Create a quote for cross-currency transfer",
    sampleRequest: `{
  "sendingCurrency": "USD",
  "receivingCurrency": "EUR",
  "sendingAmount": 100000,
  "customerId": "cust_01HQ3X5K7N2M8P"
}`,
    sampleResponse: `{
  "id": "quote_01HQ6A1B2C3D",
  "sendingAmount": { "amount": 100000, "currency": "USD" },
  "receivingAmount": { "amount": 92000, "currency": "EUR" },
  "exchangeRate": 0.92,
  "fees": { "amount": 150, "currency": "USD" },
  "expiresAt": "2025-10-20T10:35:00Z"
}`,
  },
  {
    method: "POST",
    path: "/quotes/{id}/execute",
    category: "Transfers",
    description: "Execute a previously created quote",
    sampleRequest: `{}`,
    sampleResponse: `{
  "id": "tx_01HQ7A1B2C3D",
  "quoteId": "quote_01HQ6A1B2C3D",
  "status": "PROCESSING",
  "sentAmount": { "amount": 100000, "currency": "USD" },
  "receivedAmount": { "amount": 92000, "currency": "EUR" }
}`,
  },
  {
    method: "POST",
    path: "/transfer-out",
    category: "Transfers",
    description: "Send from internal account to external destination",
    sampleRequest: `{
  "internalAccountId": "ia_01",
  "externalAccountId": "ea_01",
  "quoteId": "quote_01HQ6A1B2C3D"
}`,
    sampleResponse: `{
  "id": "tx_01HQ7A1B2C3D",
  "type": "OUTGOING",
  "status": "PROCESSING",
  "sentAmount": { "amount": 100000, "currency": "USD" },
  "receivedAmount": { "amount": 92000, "currency": "EUR" }
}`,
  },
  {
    method: "POST",
    path: "/transfer-in",
    category: "Transfers",
    description: "Pull from external account to internal account",
    sampleRequest: `{
  "internalAccountId": "ia_01",
  "externalAccountId": "ea_01",
  "amount": 50000,
  "currency": "USD"
}`,
    sampleResponse: `{
  "id": "tx_01HQ7B2C3D4E",
  "type": "INCOMING",
  "status": "PROCESSING",
  "sentAmount": { "amount": 50000, "currency": "USD" }
}`,
  },
  {
    method: "GET",
    path: "/receiver/uma/{address}",
    category: "Transfers",
    description: "Resolve a UMA address to get receiver details",
    sampleResponse: `{
  "receiverUmaAddress": "$alice@partner.grid.com",
  "receiverName": "Alice Johnson",
  "supportedCurrencies": ["USD", "BTC", "USDC"],
  "maxAmount": { "amount": 100000000, "currency": "USD" }
}`,
  },
  {
    method: "GET",
    path: "/transactions",
    category: "Transactions",
    description: "List all transactions with filters",
    sampleResponse: `{
  "data": [
    {
      "id": "tx_01HQ4A1B2C3D",
      "type": "OUTGOING",
      "status": "COMPLETED",
      "sentAmount": { "amount": 50000, "currency": "USD" },
      "receivedAmount": { "amount": 48500, "currency": "EUR" },
      "createdAt": "2025-10-18T14:30:00Z"
    }
  ]
}`,
  },
  {
    method: "POST",
    path: "/transactions/{id}/approve",
    category: "Transactions",
    description: "Approve a pending incoming transaction",
    sampleResponse: `{
  "id": "tx_01HQ4A1B2C3M",
  "status": "PROCESSING",
  "approvedAt": "2025-10-20T11:00:00Z"
}`,
  },
  {
    method: "POST",
    path: "/invitations",
    category: "Invitations",
    description: "Create a UMA invitation",
    sampleRequest: `{
  "inviterUmaAddress": "$alice@partner.grid.com",
  "inviteeUmaAddress": "$newuser@uma.me"
}`,
    sampleResponse: `{
  "id": "inv_01HQ5R1A2B3C",
  "code": "INV-A1B2C3",
  "status": "PENDING",
  "createdAt": "2025-10-20T10:00:00Z"
}`,
  },
  {
    method: "POST",
    path: "/sandbox/internal-accounts/{id}/fund",
    category: "Sandbox",
    description: "Fund an internal account in sandbox mode",
    sampleRequest: `{
  "amount": 1000000,
  "currency": "USD"
}`,
    sampleResponse: `{
  "id": "ia_01",
  "balance": { "amount": 2523400, "currency": "USD" },
  "fundedAmount": { "amount": 1000000, "currency": "USD" }
}`,
  },
  {
    method: "POST",
    path: "/webhooks/test",
    category: "Sandbox",
    description: "Send a test webhook event",
    sampleRequest: `{
  "eventType": "incoming-payment"
}`,
    sampleResponse: `{
  "success": true,
  "eventType": "incoming-payment",
  "deliveredAt": "2025-10-20T10:30:01Z"
}`,
  },
];

const methodColors: Record<string, string> = {
  GET: "bg-emerald-500/10 text-emerald-400",
  POST: "bg-blue-500/10 text-blue-400",
  PUT: "bg-yellow-500/10 text-yellow-400",
  DELETE: "bg-red-500/10 text-red-400",
};

const categories = [...new Set(endpoints.map((e) => e.category))];

export default function ApiExplorer() {
  const [selected, setSelected] = useState(endpoints[0]);
  const [activeCategory, setActiveCategory] = useState("Customers");

  const filtered = endpoints.filter((e) => e.category === activeCategory);

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-ls-gray-50 tracking-tight">
          API Explorer
        </h1>
        <p className="text-sm text-ls-gray-500 mt-1">
          Interactive reference for all Grid API endpoints with sample
          request/response payloads
        </p>
        <p className="text-xs text-ls-gray-600 mt-2 font-mono">
          Base URL: https://api.lightspark.com/grid/2025-10-13
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Endpoint List */}
        <div>
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-1 mb-4">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  const first = endpoints.find((e) => e.category === cat);
                  if (first) setSelected(first);
                }}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  activeCategory === cat
                    ? "bg-ls-sky-500/10 text-ls-sky-500"
                    : "text-ls-gray-500 hover:text-ls-gray-300 bg-ls-gray-975"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="space-y-1">
            {filtered.map((ep) => (
              <button
                key={ep.path + ep.method}
                onClick={() => setSelected(ep)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selected === ep
                    ? "bg-ls-gray-900 border border-ls-sky-500/30"
                    : "hover:bg-ls-gray-975 border border-transparent"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${methodColors[ep.method]}`}>
                    {ep.method}
                  </span>
                  <code className="text-xs font-mono text-ls-gray-300 truncate">
                    {ep.path}
                  </code>
                </div>
                <p className="text-xs text-ls-gray-500 pl-0.5">{ep.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Right: Request/Response */}
        <div className="lg:col-span-2 space-y-4">
          {/* Header */}
          <div className="bg-ls-gray-975 border border-ls-gray-900 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-2">
              <span className={`text-xs font-bold px-2 py-1 rounded ${methodColors[selected.method]}`}>
                {selected.method}
              </span>
              <code className="text-sm font-mono text-ls-gray-50">
                {selected.path}
              </code>
            </div>
            <p className="text-sm text-ls-gray-400">{selected.description}</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-ls-gray-900 text-ls-gray-500">
                Auth: HTTP Basic
              </span>
              <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-ls-gray-900 text-ls-gray-500">
                Content-Type: application/json
              </span>
            </div>
          </div>

          {/* Request */}
          {selected.sampleRequest && (
            <div className="bg-ls-gray-975 border border-ls-gray-900 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-ls-gray-900 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                  <span className="text-xs font-medium text-ls-gray-300">Request Body</span>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(selected.sampleRequest || "")}
                  className="text-xs text-ls-gray-500 hover:text-ls-gray-300"
                >
                  Copy
                </button>
              </div>
              <pre className="p-4 text-xs font-mono text-ls-gray-300 overflow-x-auto leading-relaxed">
                {selected.sampleRequest}
              </pre>
            </div>
          )}

          {/* Response */}
          <div className="bg-ls-gray-975 border border-ls-gray-900 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-ls-gray-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-xs font-medium text-ls-gray-300">Response (200 OK)</span>
              </div>
              <button
                onClick={() => navigator.clipboard.writeText(selected.sampleResponse)}
                className="text-xs text-ls-gray-500 hover:text-ls-gray-300"
              >
                Copy
              </button>
            </div>
            <pre className="p-4 text-xs font-mono text-emerald-400/80 overflow-x-auto leading-relaxed">
              {selected.sampleResponse}
            </pre>
          </div>

          {/* cURL Example */}
          <div className="bg-ls-gray-975 border border-ls-gray-900 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-ls-gray-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-400" />
                <span className="text-xs font-medium text-ls-gray-300">cURL</span>
              </div>
            </div>
            <pre className="p-4 text-xs font-mono text-orange-400/80 overflow-x-auto leading-relaxed whitespace-pre-wrap">
{`curl -X ${selected.method} \\
  'https://api.lightspark.com/grid/2025-10-13${selected.path.replace(/\{[^}]+\}/g, ":id")}' \\
  -H 'Authorization: Basic <base64(tokenId:clientSecret)>' \\
  -H 'Content-Type: application/json'${selected.sampleRequest ? ` \\
  -d '${selected.sampleRequest.replace(/\n/g, "").replace(/\s+/g, " ")}'` : ""}`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
